import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, HelpCircle } from "lucide-react";

/**
 * Small, premium onboarding tour for the Team calendar.
 *
 * - 3 steps anchored to data-testid'd elements in the calendar header.
 * - Auto-runs the first time a user opens Team mode; never auto-runs again
 *   once dismissed. Dismissed state is keyed by user.id so different
 *   accounts on the same browser are tracked independently.
 * - Subtle floating tooltip + soft ring around the target — no dark
 *   backdrop / modal feel.
 * - Repositions on scroll / resize. Recovers gracefully if the anchor
 *   element isn't in the DOM yet (e.g. responsive layout hides it).
 *
 * The tour exposes a `<TeamTourHelpButton />` so users can reopen the
 * tour after dismissing it — that lives next to the view-mode toggle.
 */

const TOUR_STEPS = [
  {
    target: '[data-testid="view-mode-toggle"]',
    title: "Bytt mellom Liste og Team",
    body:
      "Bruk dette valget for å skifte mellom den rolige listevisningen og tidslinjen med alle ansatte.",
    placement: "bottom",
  },
  {
    target: '[data-testid="staff-preset-mine"]',
    title: "Mine viser kun deg",
    body: "Trykk «Mine» for å filtrere kalenderen til dine egne bookinger.",
    placement: "bottom",
  },
  {
    // Match any staff chip — we use the first available one as the anchor.
    target: '[data-testid^="staff-chip-"]',
    title: "Filtrer på ansatte",
    body: "Slå hver ansatt av eller på for å vise akkurat det teamet du trenger.",
    placement: "bottom",
  },
];

const storageKey = (userId) => `clientflow.tour.team.dismissed.${userId || "anon"}`;

export function isTeamTourDismissed(userId) {
  try {
    return localStorage.getItem(storageKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function markTeamTourDismissed(userId) {
  try {
    localStorage.setItem(storageKey(userId), "1");
  } catch {
    /* ignore quota errors */
  }
}

export function clearTeamTourDismissed(userId) {
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    /* ignore */
  }
}

/**
 * The actual tour overlay. Controlled by `open`. When the user closes it
 * via the X, the "Skjønner"-button, or the "Ikke vis igjen"-link, we call
 * `onClose(dismiss)` so the parent can persist the choice.
 */
export function TeamTour({ open, onClose }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // Whenever a tour step opens or advances, blur any focused element so
  // its native focus ring doesn't compete with our tour ring. The most
  // common culprit on mobile is the "?" help button — the user just
  // tapped it, and its focus ring shows on the *Team* toggle nearby
  // since it sits next to it, making it look like the tour highlights
  // the wrong element.
  useEffect(() => {
    if (!open) return;
    try { document.activeElement?.blur?.(); } catch { /* ignore */ }
  }, [open, stepIndex]);

  // Track the current step's anchor rect. We re-measure on EVERY frame
  // while the tour is open (cheap — single getBoundingClientRect call)
  // so the ring + tooltip stay glued to the real target position
  // through any layout shift (sticky header settling, lazy fonts loading,
  // route-change scroll reset). No scrollIntoView — that introduced a
  // 51px race where the rect was read before the scroll completed.
  // Users are expected to see the target naturally; Step 1 (view-mode-
  // toggle), Step 2 (staff-preset-mine) and Step 3 (first staff-chip)
  // are all in the top toolbar above the timeline.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const step = TOUR_STEPS[stepIndex];
    let raf = 0;
    let stopped = false;
    let pollUntil = Date.now() + 1500; // stop polling missing element after 1.5s
    const tick = () => {
      if (stopped) return;
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect((prev) => {
          // Skip the React commit if nothing changed (sub-pixel jitter ok)
          if (
            prev &&
            Math.abs(prev.top - r.top) < 0.5 &&
            Math.abs(prev.left - r.left) < 0.5 &&
            Math.abs(prev.width - r.width) < 0.5 &&
            Math.abs(prev.height - r.height) < 0.5
          ) return prev;
          return { top: r.top, left: r.left, width: r.width, height: r.height };
        });
      } else if (Date.now() > pollUntil) {
        setRect(null);
      }
      raf = window.requestAnimationFrame(tick);
    };
    tick();
    return () => {
      stopped = true;
      window.cancelAnimationFrame(raf);
    };
  }, [open, stepIndex]);

  if (!open) return null;

  const isLast = stepIndex === TOUR_STEPS.length - 1;
  const step = TOUR_STEPS[stepIndex];

  // Tooltip placement: anchored under the target, clamped to viewport.
  // Tooltip is fixed-positioned (top/left in viewport coords); the ring
  // around the target uses the same rect.
  const TOOLTIP_W = 300;
  const GAP = 12;
  let tooltipTop = (rect?.top ?? 80) + (rect?.height ?? 0) + GAP;
  let tooltipLeft = rect
    ? rect.left + rect.width / 2 - TOOLTIP_W / 2
    : window.innerWidth / 2 - TOOLTIP_W / 2;
  if (tooltipLeft < 12) tooltipLeft = 12;
  if (tooltipLeft + TOOLTIP_W > window.innerWidth - 12) {
    tooltipLeft = window.innerWidth - TOOLTIP_W - 12;
  }
  // If anchor is too low in the viewport, flip the tooltip above it.
  if (rect && tooltipTop + 180 > window.innerHeight) {
    tooltipTop = rect.top - GAP - 160;
    if (tooltipTop < 12) tooltipTop = 12;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 pointer-events-none" data-testid="team-tour">
      {/* Subtle dim backdrop so the highlighted element pops — opacity
          stays low enough that the calendar stays readable, but
          competing focus / hover rings on neighbouring elements get
          visually muted. */}
      <div className="absolute inset-0 bg-foreground/10 pointer-events-none" aria-hidden="true" />
      {/* Soft ring around the anchor — bright primary glow with a soft
          shadow so it reads on both light and dark backgrounds. */}
      {rect && (
        <div
          className="fixed rounded-2xl pointer-events-none animate-pulse-soft"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 3px hsl(var(--primary)), 0 0 0 6px hsl(var(--background)), 0 0 22px 6px hsl(var(--primary) / 0.35)",
            transition: "top 200ms, left 200ms, width 200ms, height 200ms",
          }}
          aria-hidden="true"
        />
      )}
      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="fixed w-[300px] max-w-[calc(100vw-24px)] rounded-2xl bg-card border border-border shadow-lg p-4 pointer-events-auto animate-tour-in"
        style={{ top: tooltipTop, left: tooltipLeft }}
        role="dialog"
        aria-label={`Steg ${stepIndex + 1} av ${TOUR_STEPS.length}: ${step.title}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-primary">
            Steg {stepIndex + 1} av {TOUR_STEPS.length}
          </div>
          <button
            onClick={() => onClose({ dismiss: true })}
            aria-label="Lukk guide"
            className="p-1 -m-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
            data-testid="team-tour-close"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.75} />
          </button>
        </div>
        <h3 className="mt-1.5 font-heading font-semibold text-base">{step.title}</h3>
        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            onClick={() => onClose({ dismiss: true })}
            className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
            data-testid="team-tour-never-show"
          >
            Ikke vis igjen
          </button>
          <div className="flex items-center gap-1.5">
            {/* Step dots */}
            <div className="flex items-center gap-1 mr-1.5" aria-hidden="true">
              {TOUR_STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIndex ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"
                  }`}
                />
              ))}
            </div>
            {!isLast ? (
              <button
                onClick={() => setStepIndex(stepIndex + 1)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                data-testid="team-tour-next"
              >
                Neste
              </button>
            ) : (
              <button
                onClick={() => onClose({ dismiss: true })}
                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                data-testid="team-tour-finish"
              >
                Skjønner
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Tiny "?" button rendered next to the view-mode toggle. Clicking it
 * reopens the tour even after dismissal — for users who skipped it the
 * first time around.
 */
export function TeamTourHelpButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label="Vis guide for Team-modus"
      title="Vis guide"
      className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
      data-testid="team-tour-help-btn"
    >
      <HelpCircle className="w-4 h-4" strokeWidth={1.75} />
    </button>
  );
}
